import React from 'react'
import { createPortal } from 'react-dom'
import type { ModalProps } from 'react-native'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import ButtonIcon from '@ui-kit/components/ButtonIcon'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { h4 } from '@ui-kit/core/typography'

export type PopupBackdrop = 'dim' | 'blur'

interface Props
  extends Pick<
    ModalProps,
    'animationType' | 'onShow' | 'onDismiss' | 'onRequestClose' | 'visible' | 'children' | 'testID'
  > {
  title?: string
  isFullScreen?: boolean // when "true" the popup takes the full height and width of the screen
  isFullHeight?: boolean // ignored on web (the card is always a centered max-h-[90vh] modal)
  noClose?: boolean // when "true", the close button is hidden
  backdrop?: PopupBackdrop
  onRequestClose: ModalProps['onRequestClose']
}

const popup = tv({
  slots: {
    overlay: 'fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-backdrop p-6',
    contentContainer:
      'relative flex max-h-[90vh] w-full max-w-xl flex-col overflow-y-auto rounded-2xl bg-white px-8 pb-6 pt-8',
    header: 'flex flex-row justify-end',
    title: [h4(), 'mb-0 flex-shrink'],
  },
  variants: {
    backdrop: {
      dim: {},
      blur: {
        overlay: 'bg-[rgba(0,0,0,0.6)] backdrop-blur-[4px]',
      },
    },
    isFullScreen: {
      true: {
        overlay: 'bg-white p-0',
        contentContainer: 'max-h-full flex-1 rounded-none p-0',
      },
    },
    hasTitle: {
      true: {
        header: 'justify-between',
      },
    },
  },
})

export const Popup = (props: Props) => {
  if (!props.visible || typeof document === 'undefined') {
    return null
  }

  const { overlay, contentContainer, header, title } = popup({
    isFullScreen: props.isFullScreen,
    hasTitle: Boolean(props.title),
    backdrop: props.backdrop,
  })

  return createPortal(
    <View className={overlay()} testID={props.testID}>
      <View className={contentContainer()}>
        <View className={header()}>
          {props.title ? <Text className={title()}>{props.title}</Text> : null}
          {props.noClose ? null : (
            <ButtonIcon icon={<PhosphorIcon name="X" size={24} />} onPress={props.onRequestClose} />
          )}
        </View>

        {props.children}
      </View>
    </View>,
    document.body
  )
}
